/**
 * Página /tesoreria/cuotas — generación y listado de cuotas (S19, fix H7).
 *
 * El tesorero genera las cuotas del semestre (una por tenencia responsable
 * activa, monto desde `configuracion_cuota`) y consulta el listado del barrio.
 * Backend: POST /cuotas/generar-semestre (idempotente) + GET /cuotas/.
 */
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  condonarCuota,
  generarCuotasSemestre,
  listCuotas,
} from '../../lib/api/cuotas';
import { listLotes } from '../../lib/api/lotes';
import type { CuotaRead, EstadoCuota } from '../../lib/api/types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ModalConfirmar } from '../../components/ui/ModalConfirmar';
import { formatCLP, formatFecha } from '../../lib/format';
import { hoy } from '../../lib/tiempo';
import { Monto, Num } from '../../components/tesoreria/Monto';

// Estados terminales: no admiten condonación (ya pagada o ya condonada).
const ESTADOS_CONDONABLES: ReadonlySet<EstadoCuota> = new Set<EstadoCuota>([
  'pendiente',
  'en_abono',
  'vencida',
]);

const ETIQUETA_ESTADO: Record<EstadoCuota, string> = {
  pendiente: 'Pendiente',
  en_abono: 'En abono',
  pagada: 'Pagada',
  vencida: 'Vencida',
  condonada: 'Condonada',
};

const COLOR_ESTADO: Record<EstadoCuota, string> = {
  pendiente: 'text-state-warning',
  en_abono: 'text-state-info',
  pagada: 'text-state-success',
  vencida: 'text-state-danger',
  condonada: 'text-text-muted',
};

function mensajeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (
      typeof err.body === 'object' &&
      err.body !== null &&
      'detail' in err.body
    ) {
      return String((err.body as { detail: unknown }).detail);
    }
    return `Error ${err.status}`;
  }
  return 'Error de red';
}

/** Opciones de periodo: año actual ±1, ambos semestres, descendente. */
function periodosDisponibles(): string[] {
  // Año en timezone Santiago (conv. #16): no usar new Date() directo.
  const anioActual = Number(hoy().slice(0, 4));
  const periodos: string[] = [];
  for (let a = anioActual + 1; a >= anioActual - 1; a--) {
    periodos.push(`${a}-S2`, `${a}-S1`);
  }
  return periodos;
}

export function CuotasPage() {
  const periodos = useMemo(periodosDisponibles, []);
  const [periodo, setPeriodo] = useState<string>(
    `${hoy().slice(0, 4)}-S1`,
  );
  const [cuotas, setCuotas] = useState<CuotaRead[]>([]);
  const [lotesPorId, setLotesPorId] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generando, setGenerando] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filtroPeriodo, setFiltroPeriodo] = useState<string>('');
  // Condonación: cuota seleccionada + estado del modal.
  const [cuotaACondonar, setCuotaACondonar] = useState<CuotaRead | null>(null);
  const [condonando, setCondonando] = useState(false);
  const [errorCondonar, setErrorCondonar] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    Promise.all([
      listCuotas(filtroPeriodo ? { periodo: filtroPeriodo } : {}),
      listLotes(),
    ])
      .then(([cs, lotes]) => {
        if (cancelado) return;
        setCuotas(cs);
        setLotesPorId(
          Object.fromEntries(lotes.map((l) => [l.id, l.identificador])),
        );
      })
      .catch((err) => {
        if (!cancelado) setError(mensajeError(err));
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey, filtroPeriodo]);

  async function confirmarCondonar(motivo: string) {
    if (!cuotaACondonar) return;
    setCondonando(true);
    setErrorCondonar(null);
    try {
      await condonarCuota(cuotaACondonar.id, motivo);
      setCuotaACondonar(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setErrorCondonar(mensajeError(err));
    } finally {
      setCondonando(false);
    }
  }

  async function handleGenerar() {
    if (generando) return;
    setGenerando(true);
    setError(null);
    setOkMsg(null);
    try {
      const creadas = await generarCuotasSemestre(periodo);
      setOkMsg(`Periodo ${periodo}: ${creadas.length} cuotas generadas.`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setGenerando(false);
    }
  }

  return (
    <div className="px-4 md:px-8 py-6 space-y-5">
      <div>
        <h1 className="text-xl font-medium text-text-primary">Cuotas</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Genera las cuotas del semestre (una por casa con tenencia responsable
          activa, monto desde la configuración) y consulta el listado del barrio.
        </p>
      </div>

      {/* Generar */}
      <Card>
        <h2 className="text-sm font-medium text-text-primary mb-2">
          Generar cuotas del semestre
        </h2>
        <p className="text-xs text-text-muted mb-3">
          Operación idempotente: si el periodo ya fue generado, no duplica.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            aria-label="Periodo a generar"
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="min-w-[8rem] rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary text-sm px-3 py-2"
          >
            {periodos.map((p) => (
              <option
                key={p}
                value={p}
                className="bg-bg-sunken text-text-primary"
              >
                {p}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            onClick={handleGenerar}
            disabled={generando}
          >
            {generando ? 'Generando…' : 'Generar cuotas'}
          </Button>
        </div>
        {okMsg && (
          <div
            className="mt-3 bg-state-success/15 border-l-4 border-state-success p-3 rounded-sm text-sm text-text-primary"
            role="status"
          >
            {okMsg}
          </div>
        )}
      </Card>

      {error && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* Listado */}
      <Card>
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="text-sm font-medium text-text-primary">
            Cuotas del barrio
          </h2>
          <select
            aria-label="Filtrar por periodo"
            value={filtroPeriodo}
            onChange={(e) => setFiltroPeriodo(e.target.value)}
            className="min-w-[10rem] rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-secondary text-sm px-2 py-1"
          >
            <option value="" className="bg-bg-sunken text-text-primary">
              Todos los periodos
            </option>
            {periodos.map((p) => (
              <option
                key={p}
                value={p}
                className="bg-bg-sunken text-text-primary"
              >
                {p}
              </option>
            ))}
          </select>
        </div>
        {loading ? (
          <p className="text-sm text-text-muted">Cargando…</p>
        ) : cuotas.length === 0 ? (
          <p className="text-sm text-text-muted">
            No hay cuotas para el filtro seleccionado.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-text-muted border-b-[0.5px] border-white/[0.08]">
                  <th className="px-2 py-2 font-medium">Casa</th>
                  <th className="px-2 py-2 font-medium">Periodo</th>
                  <th className="px-2 py-2 font-medium text-right">Monto</th>
                  <th className="px-2 py-2 font-medium">Vence</th>
                  <th className="px-2 py-2 font-medium">Estado</th>
                  <th className="px-2 py-2 font-medium text-right"></th>
                </tr>
              </thead>
              <tbody>
                {cuotas.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b-[0.5px] border-white/[0.04]"
                  >
                    <td className="px-2 py-2 text-text-primary">
                      {lotesPorId[c.lote_id] ?? '—'}
                    </td>
                    <td className="px-2 py-2 text-text-secondary">
                      <Num>{c.periodo}</Num>
                      {c.es_arrastre && (
                        <span className="ml-1 text-[10px] uppercase text-text-muted">
                          arrastre
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-text-primary">
                      <Monto value={c.monto_total} />
                    </td>
                    <td className="px-2 py-2 text-text-secondary">
                      {formatFecha(c.vence_el)}
                    </td>
                    <td
                      className={`px-2 py-2 font-medium ${COLOR_ESTADO[c.estado]}`}
                    >
                      {ETIQUETA_ESTADO[c.estado]}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {ESTADOS_CONDONABLES.has(c.estado) && (
                        <button
                          type="button"
                          onClick={() => {
                            setErrorCondonar(null);
                            setCuotaACondonar(c);
                          }}
                          className="text-[11px] text-text-secondary hover:text-state-danger"
                        >
                          Condonar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {cuotaACondonar && (
        <ModalConfirmar
          title="Condonar cuota"
          mensaje={`Vas a condonar la cuota ${cuotaACondonar.periodo} de la casa ${
            lotesPorId[cuotaACondonar.lote_id] ?? '—'
          } por ${formatCLP(cuotaACondonar.monto_total)}. La comisión renuncia a cobrarla: saldrá del cálculo de morosidad y quedará registrado con tu motivo. No se puede deshacer.`}
          pedirMotivo
          motivoPlaceholder="Explica por qué se condona (ej. deuda no traspasada en la venta de la casa)…"
          motivoRequerido
          textoConfirmar={condonando ? 'Condonando…' : 'Sí, condonar'}
          variantConfirmar="danger"
          onConfirmar={confirmarCondonar}
          onCancelar={() => {
            setCuotaACondonar(null);
            setErrorCondonar(null);
          }}
          error={errorCondonar}
        />
      )}
    </div>
  );
}
